
import React from "react";
import { Check, AlertCircle } from "lucide-react";
import { QuizResult } from "@/types";

interface ScoreDisplayProps {
  result: QuizResult;
}

export const getScoreCategory = (percentage: number): string => {
  if (percentage >= 76) return "Excellent";
  if (percentage >= 51) return "High";
  if (percentage >= 26) return "Moderate";
  return "Low";
};

export const getScoreColor = (percentage: number): string => {
  if (percentage >= 76) return "text-green-600";
  if (percentage >= 51) return "text-blue-600";
  if (percentage >= 26) return "text-yellow-600";
  return "text-red-600";
};

export const getScoreRingColor = (percentage: number): string => {
  if (percentage >= 76) return "border-green-500";
  if (percentage >= 51) return "border-blue-500";
  if (percentage >= 26) return "border-yellow-500";
  return "border-red-500";
};

const ScoreDisplay: React.FC<ScoreDisplayProps> = ({ result }) => {
  return (
    <div>
      <div className="text-center mb-6">
        <div className="relative inline-flex">
          <div className={`w-32 h-32 rounded-full border-8 ${getScoreRingColor(result.percentage)} flex items-center justify-center`}>
            <span className={`text-4xl font-bold ${getScoreColor(result.percentage)}`}>{result.percentage}%</span>
          </div>
          <div className="absolute -right-2 -top-2 bg-white rounded-full p-1 shadow-md">
            {result.percentage >= 51 ? (
              <Check className="w-6 h-6 text-green-500" />
            ) : (
              <AlertCircle className="w-6 h-6 text-yellow-500" />
            )}
          </div>
        </div>
        <h3 className="text-xl font-bold mt-4 text-techne-blue">
          {result.readinessTier || getScoreCategory(result.percentage)} Readiness
        </h3>
        <p className="text-sm text-gray-600 mt-1">AI Governance Readiness</p>
      </div>

      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium text-gray-900 mb-2">What this means:</h4>
        <p className="text-gray-700 text-sm">
          {result.percentage >= 76 && (
            "Your board demonstrates excellent AI governance readiness. You have established key structures, policies, and oversight mechanisms for responsible AI use."
          )}
          {result.percentage >= 51 && result.percentage < 76 && (
            "Your board has high AI governance readiness. With some targeted improvements in specific areas, you can enhance your ability to address AI challenges."
          )}
          {result.percentage >= 26 && result.percentage < 51 && (
            "Your board has moderate AI governance readiness with several areas needing attention. Consider prioritizing the improvement recommendations."
          )}
          {result.percentage < 26 && (
            "Your board has low AI governance readiness. There are significant opportunities to strengthen your oversight capabilities."
          )}
        </p>
      </div>
    </div>
  );
};

export default ScoreDisplay;
